import type { SargassoOptions, WaveParams } from "@sargasso/core";

/** Fired by `<sargasso-canvas>` whenever its options change; enables two-way sync. */
export const SARGASSO_CHANGE_EVENT = "sargasso-change";

export interface SargassoChangeDetail {
  /** The canvas's current, fully-resolved options. */
  options: ResolvedCanvasOptions;
}

/** The subset of options a canvas exposes to controls and change events. */
export interface ResolvedCanvasOptions {
  waves: WaveParams[];
  extent: number;
  speed: number;
  steepness: number;
  contour: NonNullable<SargassoOptions["contour"]> | null;
}

/** Parse a numeric attribute; returns undefined for null/empty/NaN. */
export function parseNumberAttr(value: string | null): number | undefined {
  if (value === null || value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Parse a JSON attribute; returns undefined on null or malformed JSON. */
export function parseJsonAttr<T>(value: string | null): T | undefined {
  if (value === null || value.trim() === "") return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

type OptionParser = (value: string | null) => Partial<SargassoOptions> | undefined;

const numberOption =
  (key: "extent" | "speed" | "steepness"): OptionParser =>
  (value) => {
    const v = parseNumberAttr(value);
    return v === undefined ? undefined : ({ [key]: v } as Partial<SargassoOptions>);
  };

/** Parser per observed attribute; the keys double as the observed-attribute list. */
const OPTION_PARSERS: Record<string, OptionParser> = {
  extent: numberOption("extent"),
  speed: numberOption("speed"),
  steepness: numberOption("steepness"),
  waves: (value) => {
    const v = parseJsonAttr<SargassoOptions["waves"]>(value);
    return v === undefined ? undefined : { waves: v };
  },
  contour: (value) => {
    if (value === "none") return { contour: null };
    const v = parseJsonAttr<SargassoOptions["contour"]>(value);
    return v === undefined ? undefined : { contour: v };
  },
};

/** Map a single observed attribute to an options patch. */
export function attributeToOption(
  name: string,
  value: string | null,
): Partial<SargassoOptions> | undefined {
  return OPTION_PARSERS[name]?.(value);
}

/** The attributes `<sargasso-canvas>` observes — one per entry in {@link OPTION_PARSERS}. */
export const OBSERVED_CANVAS_ATTRIBUTES: readonly string[] = Object.keys(OPTION_PARSERS);

/** Construct a typed CustomEvent that bubbles and crosses shadow boundaries. */
export function makeEvent<T>(type: string, detail: T): CustomEvent<T> {
  return new CustomEvent<T>(type, { detail, bubbles: true, composed: true });
}
